/* Copyright 2016-2022
   Kabandy
   Author: Cedric Hoarau

    - build: npm run build-api-sandbox-app -- env-vars "KAB_API_URL=API_URL,KAB_ACCOUNTS_URL=ACCOUNTS_URL,KAB_VIEWER_URL=VIEWER_URL"
 */

import { UIApi } from "./ui-api";
import { UIBoard } from "./ui-board";
import { UIBoardCells } from "./ui-board-cells";
import { UIFile } from "./ui-file";
import { UIModel } from "./ui-models";
import { UIModelfile } from "./ui-modelfile";
import { EAuthCacheKey, UIAuth } from "./ui-auth";
import { UIVersion } from "./ui-version";
import { UIViewer } from "./ui-viewer";
import { UIWorkspace } from "./ui-workspace";
import { UIQuantity } from "./ui-quantity";
import { UIModal } from "./ui-modal";
import { UIResourceTypes } from "./ui-resources";
import { ApiRouteFormatter, getAuthCode, getAuthState, initializeKabandyWebApi, isCloudRunEnabled } from "../ApiWeb";
import { Network } from "./network";
import { OAuthClientConf } from "./oauth-client-conf";

import "./favicon.ico";

//
// --- UI Listeners

function _OnUserAuthenticated(accessToken: string): void {
    UIWorkspace.Bind(accessToken, _OnWorkspaceSelected);
    UIFile.Bind(accessToken);
}
function _OnWorkspaceSelected(accessToken: string, workspaceId: string, kabResourceType: UIResourceTypes.EKabResourceType): void {
    UIViewer.Unbind();

    // Quantity
    if( UIResourceTypes.EKabResourceType.Quantity === kabResourceType )
    {
        UIQuantity.Unbind();
        UIQuantity.Bind( accessToken, workspaceId, _OnQuantitySelected );
    }
    // Model
    else if( UIResourceTypes.EKabResourceType.Model === kabResourceType )
    {
        UIVersion.Unbind();
        UIModelfile.Unbind();
        UIModel.Unbind();
        UIModel.Bind(accessToken, workspaceId, _OnModelSelected);
    }
    // Board
    else {
        UIBoard.Unbind();
        UIBoard.Bind( accessToken, workspaceId, _OnBoardSelected );
    }
}
function _OnModelSelected(accessToken: string, workspaceId: string, modelId: string): void {
    UIViewer.Unbind();
    UIVersion.Unbind();
    UIModelfile.Unbind();
    UIModelfile.Bind(accessToken, workspaceId, modelId, _OnModelfileSelected);
}
function _OnModelfileSelected(accessToken: string, workspaceId: string, modelId: string, modelfileId: string): void {
    UIViewer.Unbind();
    UIVersion.Unbind();
    UIVersion.Bind(accessToken, workspaceId, modelId, modelfileId, _OnVersionSelected);
}
function _OnVersionSelected(accessToken: string, workspaceId: string, modelId: string, versionId: string): void {
    UIViewer.Unbind();
    UIViewer.Bind(accessToken, workspaceId, { modelId: modelId, versionIds: [versionId] } );
}
function _OnQuantitySelected(accessToken: string, workspaceId: string, quantityId: string): void {
    UIViewer.Unbind();
    UIViewer.Bind(accessToken, workspaceId, { quantityId: quantityId }, ( data: any ) => UIQuantity.OnQuantityCreated( accessToken, workspaceId, data ) );
}
function _OnBoardSelected( accessToken: string, workspaceId: string, boardId: string ): void {
    UIBoardCells.Unbind();
    UIBoardCells.Bind(accessToken, workspaceId, boardId);    
}


//
// --- Authentication code

async function _HandleAuthCode( authCode : string, authState: string ) {
    // Authorization state (CSRF protection)
    const localAuthState : string | null = localStorage.getItem( EAuthCacheKey.AuthState );
    if( localAuthState != authState ) {
        throw new Error( "ApiSandboxApp::_HandleAuthCode(): Wrong authorization state" );
    }

    if( !authCode ) {
        throw new Error( "ApiSandboxApp::_HandleAuthCode(): Missing authorization code" );
    }

    // Client ID
    const clientId : string | null = localStorage.getItem( EAuthCacheKey.ClientId );
    if(!clientId) {
        throw new Error( "ApiSandboxApp::_HandleAuthCode(): Missing client ID" );
    }

    // Redirect URL
    const redirectUrl : string | null = localStorage.getItem( EAuthCacheKey.RedirectUrl );
    if(!redirectUrl) {
        throw new Error( "ApiSandboxApp::_HandleAuthCode(): Missing redirect URL" );
    }

    // Authorization Code
    const oAuthTokenApiUrl: string = ApiRouteFormatter.FormatAccountsApiTokenUrl(isCloudRunEnabled());
    let oAuthTokenOptions: { grantType: string, redirectUri: string, clientId: string, code: string, codeVerifier?: string, clientSecret?: string } = {
        grantType: "authorization_code",
        redirectUri: redirectUrl,
        clientId: clientId,
        code: authCode
    }; 

    // Authorization Code Flow + PKCE
    const localCodeVerifier : string | null = localStorage.getItem( EAuthCacheKey.CodeVerifier );
    if( localCodeVerifier && localCodeVerifier !== "" ) {
        oAuthTokenOptions.codeVerifier = localCodeVerifier;
    }
    // Simple Authorization Code Flow 
    else {
        // Client Secret
        const clientSecret : string | null = localStorage.getItem( EAuthCacheKey.ClientSecret );
        if(!clientSecret) {
            throw new Error( "ApiSandboxApp::_HandleAuthCode(): Missing redirect client Secret" );
        }

        oAuthTokenOptions.clientSecret = clientSecret;
    }

    const { accessToken, expiresIn, error } : { accessToken?: string; refreshToken?: string; expiresIn?: string, error?: any } = await Network.PostAccountsApiToken( oAuthTokenApiUrl, oAuthTokenOptions );
    if( error ) {
        console.error( "Can't get token", error );
        return;
    }

    if( accessToken ) {
        UIModal.Bind();
        UIApi.Bind();
        _OnUserAuthenticated( accessToken );
    }
}

//
// --- Entry point

(async function _Main() {
    try {
        // API configuration
        await initializeKabandyWebApi();

        // OAuth client configuration
        OAuthClientConf.Init();

        // Authorization Code
        const authCode : string = getAuthCode();
        if( authCode ) {
            const authState : string = getAuthState();
            await _HandleAuthCode( authCode, authState );
            return;
        }
            
        // Clear local storage
        localStorage.clear(); 

        UIApi.Bind();
        UIAuth.Bind(_OnUserAuthenticated);
    } catch (error) {
        console.error(`ApiSandboxApp:_Main(): error: ${error}`);
    }
})();